// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { useNavigate } from 'react-router-dom';

const HowItWorksPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span>
              </span>
            </button>
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Home</button>
              <button onClick={() => navigate('/how-it-works')} className="text-blue-600 font-bold">How It Works</button>
              <button onClick={() => navigate('/about')} className="text-gray-700 hover:text-blue-600 font-semibold transition">About</button>
              <button onClick={() => navigate('/pricing')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Pricing</button>
              <button onClick={() => navigate('/contact')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Contact</button>
              <button onClick={() => navigate('/app')} className="px-6 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg hover:shadow-lg transition">Sign In</button>
            </div>
            <div className="flex md:hidden items-center gap-3">
              <button onClick={() => navigate('/app')} className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-bold rounded-lg">Sign In</button>
              <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-blue-600 font-semibold whitespace-nowrap">← Home</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            How PricePAL Compare Works
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Save hundreds of pounds on groceries and fuel in just 3 simple steps
          </p>
        </div>
      </section>

      {/* 3 Steps Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-16">
          {/* Step 1 */}
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full font-bold mb-4">
                STEP 1
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                📱 Scan or Search for Products
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Use your mobile phone camera to scan product barcodes whilst shopping, or search by product name from home. PricePAL Compare instantly recognises over 5,000 UK products — groceries and household items.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Scan barcodes in-store with your phone camera</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Search by product name, brand, or category</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Save frequently bought items to your list</span>
                </li>
              </ul>
            </div>
            <div className="md:w-1/2">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl p-8 shadow-xl">
                <div className="text-6xl mb-4">📱</div>
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="font-mono text-sm text-gray-500 mb-2">Scanning...</div>
                  <div className="h-2 bg-blue-600 rounded-full mb-4 animate-pulse"></div>
                  <div className="font-bold text-lg">Heinz Beanz 415g</div>
                  <div className="text-sm text-gray-600">Found in 8 retailers</div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col md:flex-row-reverse items-center gap-8">
            <div className="md:w-1/2">
              <div className="inline-block px-4 py-2 bg-green-100 text-green-600 rounded-full font-bold mb-4">
                STEP 2
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                🤖 Compare Prices Instantly
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Our system compares prices across major UK supermarkets and fuel stations in real-time. See exactly where you'll save money and by how much.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="font-semibold text-gray-900 mb-2">We compare prices from:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span>• Tesco</span>
                  <span>• Sainsbury's</span>
                  <span>• Asda</span>
                  <span>• Morrisons</span>
                  <span>• Aldi</span>
                  <span>• Lidl</span>
                  <span>• Waitrose</span>
                  <span>• Co-op</span>
                  <span>• Iceland</span>
                  <span>• Ocado</span>
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="bg-gradient-to-br from-green-100 to-green-50 rounded-2xl p-8 shadow-xl">
                <div className="text-6xl mb-4">💰</div>
                <div className="space-y-2">
                  <div className="bg-green-100 border-2 border-green-400 rounded-lg p-3 flex justify-between items-center">
                    <span className="font-semibold">Aldi</span>
                    <span className="text-green-700 font-bold">£0.85 ✓ BEST</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 flex justify-between items-center">
                    <span className="font-semibold">Lidl</span>
                    <span className="font-bold">£0.89</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 flex justify-between items-center">
                    <span className="font-semibold">Tesco</span>
                    <span className="font-bold">£0.95</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 flex justify-between items-center">
                    <span className="font-semibold">Sainsbury's</span>
                    <span className="font-bold">£1.10</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                  <span className="text-green-600 font-bold text-lg">Save £0.25 per item!</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2">
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full font-bold mb-4">
                STEP 3
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                💵 Shop Smart & Save Money
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Buy from the cheapest retailer for each product, or see which supermarket gives you the best overall basket price. Track your savings over time and watch the pounds add up!
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span><strong>Shopping Lists:</strong> Create lists and compare total basket costs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span><strong>Price Alerts:</strong> Get notified when items go on sale</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span><strong>Savings Tracker:</strong> See exactly how much you've saved</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span><strong>Fuel Savings:</strong> Track petrol discounts from loyalty programmes</span>
                </li>
              </ul>
            </div>
            <div className="md:w-1/2">
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 rounded-2xl p-8 shadow-xl">
                <div className="text-6xl mb-4">📊</div>
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <h3 className="font-bold text-xl mb-4">Your Savings This Month</h3>
                  <div className="text-5xl font-bold text-green-600 mb-2">£247</div>
                  <div className="text-gray-600 mb-4">Saved across 12 shopping trips</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Average savings per shop:</span>
                      <span className="font-semibold">£20.58</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total saved this year:</span>
                      <span className="font-semibold text-green-600">£1,423</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            More Ways to Save
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">🔔</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Price Drop Alerts</h3>
              <p className="text-gray-600">
                Get instant notifications when your favourite items go on sale or drop in price.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">📋</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Shopping Lists</h3>
              <p className="text-gray-600">
                Create lists and automatically see which supermarket offers the best total price.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">📈</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Price History</h3>
              <p className="text-gray-600">
                See historical price trends to know if a "deal" is actually a good price.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">⛽</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fuel Rewards Tracking</h3>
              <p className="text-gray-600">
                Track and optimise petrol discounts from Tesco Clubcard and Sainsbury's Nectar.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">👨‍👩‍👧‍👦</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Family Sharing</h3>
              <p className="text-gray-600">
                Share shopping lists and savings with family members on our Family Plan.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">📱</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mobile & Web</h3>
              <p className="text-gray-600">
                Access PricePAL Compare on any device - at home or whilst shopping in-store.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-green-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Saving?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join our growing community of UK shoppers comparing prices and finding better deals
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-12 py-4 bg-white text-blue-600 text-xl font-bold rounded-xl hover:shadow-2xl transition-all transform hover:scale-105"
          >
            Start Your 7-Day Free Trial
          </button>
          <p className="text-blue-100 mt-4 text-sm">
            7-day free trial · Card required · Cancel anytime · Full access to all features
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 pb-20 sm:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            <div>
              <h3 className="text-lg font-bold mb-3">PricePAL Compare</h3>
              <p className="text-gray-400 text-sm">
                Helping UK families save money on groceries.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-3">Quick Links</h3>
              <div className="space-y-2 text-sm">
                <button onClick={() => navigate('/how-it-works')} className="block text-gray-400 hover:text-white transition">How It Works</button>
                <button onClick={() => navigate('/about')} className="block text-gray-400 hover:text-white transition">About Us</button>
                <button onClick={() => navigate('/pricing')} className="block text-gray-400 hover:text-white transition">Pricing</button>
                <button onClick={() => navigate('/contact')} className="block text-gray-400 hover:text-white transition">Contact</button>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-3">Legal</h3>
              <div className="space-y-2 text-sm">
                <button onClick={() => navigate('/privacy')} className="block text-gray-400 hover:text-white transition">Privacy Policy</button>
                <button onClick={() => navigate('/terms')} className="block text-gray-400 hover:text-white transition">Terms & Conditions</button>
                <button onClick={() => navigate('/cookies')} className="block text-gray-400 hover:text-white transition">Cookie Policy</button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 text-center text-sm text-gray-400">
            <p className="mb-2">PricePAL Compare is an independent price comparison platform and is not affiliated with or endorsed by any supermarket.</p>
            <p>© 2026 PricePAL Compare Ltd. All rights reserved. | Company No: 17031232 | ICO Registration: ZC094153</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;
